// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import Button from 'shared/components/common/Button';
import { Formik, Form, FieldGroup } from 'shared/components/form';
import SendPhoneVerificationMutation from 'shared/mutations/SendPhoneVerification';
import ConfirmPhoneVerificationMutation from 'shared/mutations/ConfirmPhoneVerification';
import EnableTwoFactorMutation from 'shared/mutations/EnableTwoFactor';
import { colors } from 'shared/styleguide';

type Props = {
  role: string,
  twoFactorEnabled: boolean,
  phoneVerified: boolean,
  phone: ?string,
  onUpdated: (?string) => void,
  onError: (message: string) => void,
};

const Section = styled.div`
  margin: 24px 0;
`;

const Title = styled.span`
  display: block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
`;

const Status = styled.span`
  display: block;
  font-size: 14px;
  color: ${colors.charcoalGrey};
  margin-bottom: 16px;
`;

const VerifiedStatus = styled(Status)`
  color: #4caf50;
`;

export default function TwoFactorProfileSection({
  role,
  twoFactorEnabled,
  phoneVerified,
  phone,
  onUpdated,
  onError,
}: Props) {
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [enablingTwoFactor, setEnablingTwoFactor] = useState(false);
  const [changingPhone, setChangingPhone] = useState(!phoneVerified);

  if (!twoFactorEnabled) {
    if (role !== 'SUPERUSER') {
      return null;
    }

    return (
      <Section>
        <Title>{i18n.t('Two-Factor Authentication')}</Title>
        <Status>
          {i18n.t(
            'Add an extra layer of security by requiring a verification code at sign-in.',
          )}
        </Status>
        <Button
          theme="border"
          loading={enablingTwoFactor}
          onClick={async () => {
            setEnablingTwoFactor(true);
            try {
              await EnableTwoFactorMutation.commit({ variables: {} });
              onUpdated(
                i18n.t(
                  'Two-factor authentication enabled. Verify your phone number to complete setup.',
                ),
              );
            } catch (e) {
              onError(e.message || e);
            } finally {
              setEnablingTwoFactor(false);
            }
          }}
        >
          {i18n.t('Enable Two-Factor Authentication')}
        </Button>
      </Section>
    );
  }

  return (
    <Section>
      <Title>{i18n.t('Two-Factor Authentication')}</Title>
      {phoneVerified && !changingPhone ? (
        <>
          <VerifiedStatus>
            {i18n.t('2FA is required for your account.')}
            {phone ? ` ${i18n.t('Phone')}: ${phone}` : ''}
          </VerifiedStatus>
          <Button theme="border" onClick={() => setChangingPhone(true)}>
            {i18n.t('Change phone number')}
          </Button>
        </>
      ) : (
        <>
          <Status>
            {phoneVerified
              ? i18n.t('Enter a new phone number and verify it with a code.')
              : i18n.t(
                  'Verify your phone number to complete two-factor authentication setup.',
                )}
          </Status>
          <Formik
            initialValues={{ phone: '', code: '' }}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              try {
                await ConfirmPhoneVerificationMutation.commit({
                  variables: {
                    input: {
                      phone: values.phone.trim(),
                      code: values.code.trim(),
                    },
                  },
                });
                setChangingPhone(false);
                setCodeSent(false);
                onUpdated();
              } catch (e) {
                onError(e.message || e);
              } finally {
                setSubmitting(false);
              }
            }}
            render={({ values, isSubmitting, handleSubmit }) => (
              <Form>
                <FieldGroup
                  label={i18n.t('Phone Number')}
                  name="phone"
                  placeholder="+15551234567"
                  description={i18n.t('Include country code, e.g. +1 for US')}
                />
                {!codeSent ? (
                  <Button
                    type="button"
                    theme="border"
                    loading={sendingCode}
                    disabled={!values.phone.trim()}
                    onClick={async () => {
                      setSendingCode(true);
                      try {
                        await SendPhoneVerificationMutation.commit({
                          variables: {
                            input: { phone: values.phone.trim() },
                          },
                        });
                        setCodeSent(true);
                      } catch (e) {
                        onError(e.message || e);
                      } finally {
                        setSendingCode(false);
                      }
                    }}
                  >
                    {i18n.t('Send Verification Code')}
                  </Button>
                ) : (
                  <>
                    <FieldGroup
                      label={i18n.t('Verification Code')}
                      name="code"
                      placeholder="123456"
                    />
                    <Button
                      type="submit"
                      theme="blue"
                      loading={isSubmitting}
                      disabled={!values.code.trim()}
                      onClick={handleSubmit}
                    >
                      {i18n.t('Verify Phone Number')}
                    </Button>
                  </>
                )}
                {phoneVerified && (
                  <div style={{ marginTop: 12 }}>
                    <Button
                      type="button"
                      theme="border"
                      onClick={() => {
                        setChangingPhone(false);
                        setCodeSent(false);
                      }}
                    >
                      {i18n.t('Cancel')}
                    </Button>
                  </div>
                )}
              </Form>
            )}
          />
        </>
      )}
    </Section>
  );
}
